/**
 * `@/components/ui/Dialog` — same public API, no Radix.
 *
 * The Next.js original is built on `@radix-ui/react-dialog`. That package is
 * not a dependency of the SPA and adding one is out of scope for the port, so
 * this is a faithful re-implementation of the ONE component every call site
 * actually uses: `<Dialog open onOpenChange title description width footer>`.
 *
 * Resolving `@radix-ui/react-dialog` from the Next.js repo's `node_modules`
 * (which Node's upward lookup will happily do) is NOT a workaround: it drags a
 * SECOND copy of React into the bundle and every hook inside the dialog throws
 * `Cannot read properties of null (reading 'useRef')`. Verified, not assumed.
 * Props, markup and class names are byte-for-byte the original's, so the
 * rendered result is identical (FE-02) and no call site changed (FE-03).
 *
 * The three things Radix was doing that had to be re-created by hand:
 *
 *  1. **Portal** — `createPortal` into `document.body`, so a dialog opened
 *     from deep inside a `overflow-hidden` card is not clipped.
 *  2. **Presence** — `globals.css` gates its enter/exit keyframes on
 *     `[data-state="open"] / [data-state="closed"]` and relies on the element
 *     staying mounted until `animationend`. `useDialogPresence` reproduces
 *     exactly that: flip to `closed`, wait for the animation to finish, then
 *     unmount. (`useDismissDelay` at the call sites bridges the separate case
 *     where the PARENT unmounts the whole dialog.)
 *  3. **Dismissal** — Escape and an overlay click both call
 *     `onOpenChange(false)`, matching Radix's defaults. Focus is moved into
 *     the panel on open and restored to the previously-focused element on
 *     close, and body scroll is locked while any dialog is open.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
  footer?: ReactNode;
}

const widths = {
  sm: "sm:w-[420px]",
  md: "sm:w-[560px]",
  lg: "sm:w-[720px]",
  xl: "sm:w-[920px]",
};

/** How many dialogs are open — body scroll unlocks only when the last closes. */
let openCount = 0;

/**
 * `mounted` stays true through the exit animation; `state` is what the CSS
 * reads. Mirrors Radix's `Presence` for the specific case of a CSS animation
 * declared on `[data-state="closed"]`.
 */
function useDialogPresence(open: boolean) {
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<"open" | "closed">(open ? "open" : "closed");

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Next frame, so the element mounts as `closed` and then flips — the
      // animation-name change is what starts the enter animation.
      const raf = requestAnimationFrame(() => setState("open"));
      return () => cancelAnimationFrame(raf);
    }
    setState("closed");
    return undefined;
  }, [open]);

  // Fallback unmount for the case where the animation never fires (reduced
  // motion, a browser that skips it): 250ms comfortably outlasts the 150ms
  // exit keyframes in globals.css.
  useEffect(() => {
    if (open || !mounted) return undefined;
    const timer = setTimeout(() => setMounted(false), 250);
    return () => clearTimeout(timer);
  }, [open, mounted]);

  const onAnimationEnd = useCallback(() => {
    if (!open) setMounted(false);
  }, [open]);

  return { mounted, state, onAnimationEnd };
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = "md",
  footer,
}: DialogProps) {
  const { mounted, state, onAnimationEnd } = useDialogPresence(open);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  // Escape to dismiss — Radix's default `onEscapeKeyDown` behaviour.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onOpenChange(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  // Scroll lock + focus management, refcounted so nested dialogs behave.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    openCount += 1;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      clearTimeout(focusTimer);
      openCount -= 1;
      if (openCount === 0) document.body.style.overflow = previousOverflow;
      restoreFocusTo.current?.focus?.();
    };
  }, [open]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        className="dialog-overlay"
        data-state={state}
        onMouseDown={() => onOpenChange(false)}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-state={state}
        onAnimationEnd={onAnimationEnd}
        className={cn(
          // SDR dialog: 14px radius, cp surface + line border, deep shadow
          "dialog-content border border-[var(--cp-line)] bg-[var(--cp-surface)] shadow-[0_24px_60px_rgba(15,23,42,.22)]",
          "rounded-none focus:outline-none sm:rounded-[14px]",
          widths[width],
        )}
      >
        {(title || description) && (
          <div className="sticky top-0 z-10 flex items-start justify-between gap-2 border-b border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 sm:px-6 sm:py-4">
            <div className="min-w-0 flex-1">
              {title && (
                <h2 className="text-[15px] font-bold tracking-[-0.01em] text-[var(--cp-ink)]">
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-0.5 text-xs text-[var(--cp-ink-3)]">{description}</p>
              )}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
              className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--cp-ink-3)] hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        {footer && (
          <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
